import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Salud24h } from '../graficos/Salud24h';
import { tx } from '../i18n';
import { ConsolaSsh } from '../ui/ConsolaSsh';
import { pedir } from '../lib/api';
import { CANAL, estadoVivo, LICENCIA, permisos, textoError, tonoDisco, tonoLicencia } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { coma, cuando, desde, hace, miles, pc, soloDia } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import type { ApplianceDetalle, PuntoSalud } from '../tipos';
import { CodigoDeAlta } from '../ui/CodigoDeAlta';
import { Actualizar } from './Actualizar';
import { Licencia } from './Licencia';
import { Soporte } from './Soporte';
import { Cabecera, Consulta, Pastilla, useAviso, useCambio, useRefrescar, Vacio, Ventana } from '../ui/piezas';

/**
 * El nombre para entrar desde internet (`ews` → `ews.knoc360.com`). Uno por appliance: un
 * appliance atiende varios clientes y el login decide qué ve cada uno.
 */
function NombreEnInternet({
  a,
  editable,
  alGuardar,
}: {
  a: ApplianceDetalle;
  editable: boolean;
  alGuardar: (dominio: string | null) => Promise<void>;
}) {
  const [texto, setTexto] = useState(a.dominio ?? '');
  const [guardando, setGuardando] = useState(false);
  if (!a.nombres_dominio) return null;
  const limpio = texto.trim().toLowerCase();
  const guardar = async (d: string | null) => {
    setGuardando(true);
    await alGuardar(d);
    setGuardando(false);
  };
  return (
    <section className="panel nombre-internet">
      <h2 className="rotulo">
        {tx.appliance.direccionInternet}
        <span className="aux">
          <Pastilla tono={a.direccion ? 'arriba' : 'desconocido'}>{a.direccion ? tx.appliance.conNombre : tx.appliance.sinNombre}</Pastilla>
        </span>
      </h2>
      {editable ? (
        <form
          className="en-linea"
          onSubmit={(e) => {
            e.preventDefault();
            if (limpio && limpio !== a.dominio) void guardar(limpio);
          }}
        >
          <label className="solo-lector" htmlFor="dominio">
            {tx.appliance.nombreInternet}
          </label>
          <input
            id="dominio"
            className="entrada num"
            autoComplete="off"
            spellCheck={false}
            maxLength={40}
            value={texto}
            placeholder={tx.appliance.nombreEjemplo}
            onChange={(e) => setTexto(e.target.value)}
          />
          <span className="sufijo num">.{a.nombres_dominio}</span>
          <button className="btn prim" disabled={guardando || !limpio || limpio === a.dominio}>
            {tx.appliance.guardarNombre}
          </button>
          {a.dominio && (
            <button type="button" className="btn fantasma" disabled={guardando} onClick={() => void guardar(null)}>
              {tx.appliance.quitarNombre}
            </button>
          )}
          {a.direccion && (
            <a className="num" href={a.direccion} target="_blank" rel="noreferrer">
              {a.direccion}
            </a>
          )}
        </form>
      ) : a.direccion ? (
        <a className="num" href={a.direccion} target="_blank" rel="noreferrer">
          {a.direccion}
        </a>
      ) : (
        <p>{tx.appliance.sinNombreInternet}</p>
      )}
      <p className="nota">{tx.appliance.nombreAyuda}</p>
    </section>
  );
}

/** Un dato de la ficha: el rótulo chico y el valor. */
function Dato({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="dato">
      <dt>{titulo}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Un número grande de la salud actual, con el color que le corresponde. */
function Medida({ titulo, valor, nota, tono = 'desconocido' }: { titulo: string; valor: string; nota?: string; tono?: string }) {
  return (
    <div className={`medida ${tono}`}>
      <div className="t">{titulo}</div>
      <div className="n num">{valor}</div>
      {nota && <small>{nota}</small>}
    </div>
  );
}

/**
 * Las notas de la ficha: se guardan solo si cambiaron, y se dice si se guardaron. Quien la
 * usa le pasa las notas como `key`: si cambiaron en otro lado, el campo arranca de nuevo
 * con lo que dice la API en vez de quedarse mostrando algo viejo.
 */
function Notas({ valor, alGuardar }: { valor: string | null; alGuardar: (notas: string) => Promise<void> }) {
  const [texto, setTexto] = useState(valor ?? '');
  const [guardando, setGuardando] = useState(false);
  const cambio = texto !== (valor ?? '');
  return (
    <div className="notas">
      <label className="campo" htmlFor="notas">
        {tx.comun.notas}
        <textarea
          id="notas"
          className="entrada"
          rows={3}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={tx.appliances.notasEjemplo}
        />
      </label>
      {cambio && (
        <div className="notas-acc">
          <button type="button" className="btn fantasma chico" onClick={() => setTexto(valor ?? '')}>
            {tx.appliance.descartar}
          </button>
          <button
            type="button"
            className="btn prim chico"
            disabled={guardando}
            onClick={async () => {
              setGuardando(true);
              await alGuardar(texto.trim());
              setGuardando(false);
            }}
          >
            {guardando ? 'Saving…' : tx.appliance.guardarNotas}
          </button>
        </div>
      )}
    </div>
  );
}

const PESTANAS = ['salud', 'actualizaciones', 'internet', 'licencia', 'soporte', 'consola', 'detalles'] as const;
type Pestana = (typeof PESTANAS)[number];

export function Appliance() {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const { usuario } = useSesion();
  const puede = permisos(usuario?.rol);
  const [aviso, avisar] = useAviso();
  const refrescar = useRefrescar();
  const cambiar = useCambio(avisar, () => refrescar('/appliances', '/resumen', '/bitacora'));
  const [codigo, setCodigo] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState<'suspender' | 'revocar' | 'borrar' | null>(null);

  const detalle = useConsulta<ApplianceDetalle>(`/appliances/${id}`, { refetchInterval: 60_000 });
  const serie = useConsulta<PuntoSalud[]>(`/appliances/${id}/salud`, { refetchInterval: 300_000 }, { horas: 24 });
  // si el cliente dio permiso (decisión 19), desde acá se entra a SU app, no a una copia
  const red = useConsulta<{ nivel: string; hora: string | null }>(`/red/${id}`, { refetchInterval: 60_000 });
  const a = detalle.data;
  const s = a?.salud ?? null;
  const e = a ? estadoVivo(a) : null;
  // Borrar solo tiene sentido con uno que nunca llegó a hablar: si habló, se revoca.
  const nuncaHablo = !!a && !a.hola && !a.visto;
  // la pestaña va en la dirección: al volver o compartir el enlace se abre la misma
  const [params, setParams] = useSearchParams();
  const pedida = params.get('pestana') as Pestana | null;
  const pestana: Pestana = pedida && PESTANAS.includes(pedida) ? pedida : 'salud';

  return (
    <div className="pagina">
      <Cabecera
        titulo={a?.nombre ?? 'Appliance'}
        accion={
          a && (
            <>
              {puede.abrirRed && red.data?.nivel && red.data.nivel !== 'ninguno' && a.en_linea && (
                <Link className="btn contorno" to={`/appliances/${a.id}/red`}>
                  {tx.appliance.abrirSuApp}
                </Link>
              )}
              {puede.pedirCodigo && a.estado !== 'revocado' && (
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    void cambiar(async () => {
                      const r = await pedir<{ codigo: string }>(`/appliances/${a.id}/codigo`, { metodo: 'POST' });
                      setCodigo(r.codigo);
                    })
                  }
                >
                  {tx.appliance.codigoNuevo}
                </button>
              )}
              {puede.suspender && a.estado === 'suspendido' && (
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    void cambiar(
                      () => pedir(`/appliances/${a.id}`, { metodo: 'PATCH', cuerpo: { estado: 'activo' } }),
                      tx.appliance.activado(a.nombre),
                    )
                  }
                >
                  {tx.appliance.activar}
                </button>
              )}
              {puede.suspender && a.estado === 'activo' && (
                <button type="button" className="btn" onClick={() => setConfirmar('suspender')}>
                  {tx.appliance.suspender}
                </button>
              )}
              {puede.revocar && a.estado !== 'revocado' && (
                <button type="button" className="btn peligro" onClick={() => setConfirmar('revocar')}>
                  {tx.appliance.revocar}
                </button>
              )}
              {puede.borrar && nuncaHablo && (
                <button type="button" className="btn peligro" onClick={() => setConfirmar('borrar')}>
                  {tx.appliance.borrar}
                </button>
              )}
            </>
          )
        }
      >
        <Link to="/appliances">{tx.appliance.volver}</Link>
      </Cabecera>
      {aviso}

      <Consulta q={detalle}>
        {a && e && (
          <>
            {/* lo importante de un vistazo: quién es, cómo está y su salud, en una franja */}
            <section className="panel ap-franja">
              <div className="ap-quien">
                <Pastilla tono={e.tono}>{e.texto}</Pastilla>
                <dl className="ficha ap-ficha">
                  <Dato titulo={tx.comun.organizacion}>{a.organizacion}</Dato>
                  <Dato titulo={tx.comun.version}>{a.version ?? tx.appliance.noReportadaTodavia}</Dato>
                  <Dato titulo={tx.comun.ultimoContacto}>{a.visto ? hace(a.visto) : tx.appliance.nuncaConecto}</Dato>
                  <Dato titulo={tx.appliance.prendidoDesde}>{a.arranque ? desde(a.arranque) : '—'}</Dato>
                </dl>
                {s?.licencia && (
                  <p className="licencia">
                    <Pastilla tono={tonoLicencia(s.licencia.estado)}>{LICENCIA[s.licencia.estado ?? ''] ?? tx.licencia.titulo}</Pastilla>
                    {/* la licencia vence un día, no una hora: mostrarla con hora la corría a la anterior */}
                    {s.licencia.vence && (
                      <span className="d">
                        {tx.appliance.licenciaVence} {soloDia(s.licencia.vence)}
                      </span>
                    )}
                  </p>
                )}
              </div>
              {!s ? (
                <Vacio>{a.estado === 'alta' ? tx.appliance.noSeDioDeAlta : tx.appliance.sinSalud}</Vacio>
              ) : (
                <div className="medidas ap-medidas">
                  <Medida titulo={tx.appliance.cpu} valor={pc(s.cpu_pc)} tono={(s.cpu_pc ?? 0) > 85 ? 'caido' : 'arriba'} />
                  <Medida titulo={tx.appliance.memoria} valor={pc(s.memoria_pc)} tono={(s.memoria_pc ?? 0) > 90 ? 'parcial' : 'arriba'} />
                  <Medida
                    titulo={tx.comun.disco}
                    valor={pc(s.disco_pc)}
                    nota={s.disco_libre_gb !== undefined ? tx.appliance.libres(coma(s.disco_libre_gb)) : undefined}
                    tono={tonoDisco(s.disco_pc)}
                  />
                  <Medida titulo={tx.appliance.base} valor={s.base_mb !== undefined ? `${miles(s.base_mb)} MB` : '—'} />
                  <Medida
                    titulo={tx.comun.equipos}
                    valor={miles(s.equipos)}
                    nota={tx.appliance.equiposCaidos(miles(s.equipos_caidos ?? 0))}
                    tono={(s.equipos_caidos ?? 0) > 0 ? 'caido' : 'arriba'}
                  />
                  <Medida
                    titulo={tx.appliance.motores}
                    valor={`${miles(s.motores_conectados)} / ${miles(s.motores)}`}
                    nota={tx.appliance.conectados}
                    tono={(s.motores ?? 0) > (s.motores_conectados ?? 0) ? 'caido' : 'arriba'}
                  />
                </div>
              )}
            </section>

            {/* lo demás, en pestañas: no una columna que crece hacia abajo */}
            <nav className="pestanas" role="tablist" aria-label={tx.appliance.pestanas}>
              {PESTANAS.filter((p) => p !== 'consola' || puede.abrirRed).map((p) => (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  aria-selected={pestana === p}
                  className={pestana === p ? 'activa' : undefined}
                  onClick={() => setParams({ pestana: p }, { replace: true })}
                >
                  {tx.appliance.pestana[p]}
                </button>
              ))}
            </nav>

            {pestana === 'salud' && (
              <div className="dos-cols">
                <section className="panel">
                  <h2 className="rotulo">{tx.appliance.ultimas24}</h2>
                  <Consulta q={serie}>
                    {/* la ventana termina cuando llegaron los datos, no en un Date.now() que
                        se mueve en cada repintado: el gráfico no tiembla entre renders */}
                    <Salud24h puntos={serie.data ?? []} hasta={serie.dataUpdatedAt} />
                  </Consulta>
                </section>
                <section className="panel">
                  <h2 className="rotulo">{tx.appliance.ultimosErrores}</h2>
                  {!s?.errores?.length ? (
                    <Vacio>{tx.appliance.sinErrores}</Vacio>
                  ) : (
                    <ul className="errores">
                      {s.errores.map((err, i) => (
                        <li key={`${err.cuando}-${i}`}>
                          <span className="chip">{err.que}</span>
                          <span className="texto">{textoError(err)}</span>
                          <span className="d">{cuando(err.cuando)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            )}

            {pestana === 'actualizaciones' && (
              <>
                <section className="panel ap-canal">
                  <h2 className="rotulo">{tx.appliance.canalDeVersiones}</h2>
                  <div className="ap-canal-fila">
                    {puede.editarAppliance ? (
                      <div className="seg" role="group" aria-label={tx.appliance.canalDeVersiones}>
                        {(['estable', 'beta'] as const).map((c) => (
                          <button
                            key={c}
                            type="button"
                            aria-pressed={a.canal === c}
                            onClick={() =>
                              a.canal !== c &&
                              void cambiar(() => pedir(`/appliances/${a.id}`, { metodo: 'PATCH', cuerpo: { canal: c } }), tx.appliance.canalCambiado)
                            }
                          >
                            {tx.comun.canales[c]}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <b>{CANAL[a.canal] ?? a.canal}</b>
                    )}
                    <p className="nota">{a.canal === 'beta' ? tx.appliance.canalBetaQue : tx.appliance.canalEstableQue}</p>
                  </div>
                </section>
                <Actualizar
                  applianceId={a.id}
                  version={a.version}
                  canal={a.canal}
                  ventana={a.ventana ?? null}
                  alCambiar={(t) => void cambiar(() => refrescar(`/appliances/${a.id}`), t)}
                />
              </>
            )}

            {pestana === 'internet' && (
              <NombreEnInternet
                key={a.dominio ?? ''}
                a={a}
                editable={puede.editarAppliance}
                alGuardar={(dominio) =>
                  cambiar(
                    () => pedir(`/appliances/${a.id}/dominio`, { metodo: 'PUT', cuerpo: { dominio } }),
                    dominio ? tx.appliance.nombreGuardado : tx.appliance.nombreQuitado,
                  )
                }
              />
            )}

            {pestana === 'licencia' && (
              <Licencia applianceId={a.id} enLinea={a.en_linea} alCambiar={(t) => void cambiar(() => Promise.resolve(), t)} />
            )}

            {pestana === 'soporte' && (
              <Soporte
                applianceId={a.id}
                enLinea={a.en_linea}
                soporte={a.soporte ?? null}
                alCambiar={(t) => void cambiar(() => refrescar(`/appliances/${a.id}`), t)}
              />
            )}

            {pestana === 'consola' && puede.abrirRed && <ConsolaSsh applianceId={a.id} nombre={a.nombre} />}

            {pestana === 'detalles' && (
              <section className="panel">
                <h2 className="rotulo">{tx.appliance.detalles}</h2>
                <dl className="ficha">
                  <Dato titulo={tx.appliance.sistema}>{a.so ?? '—'}</Dato>
                  <Dato titulo={tx.appliance.prendidoDesde}>{a.arranque ? `${cuando(a.arranque)} (${desde(a.arranque)})` : '—'}</Dato>
                  <Dato titulo={tx.comun.ultimoContacto}>{a.visto ? `${cuando(a.visto)} · ${hace(a.visto)}` : tx.appliance.nuncaConecto}</Dato>
                  <Dato titulo={tx.appliance.creadoEnElCentro}>{cuando(a.creado)}</Dato>
                </dl>
                {puede.editarAppliance ? (
                  <Notas
                    key={a.notas ?? ''}
                    valor={a.notas}
                    alGuardar={(notas) =>
                      cambiar(() => pedir(`/appliances/${a.id}`, { metodo: 'PATCH', cuerpo: { notas } }), tx.appliance.notasGuardadas)
                    }
                  />
                ) : (
                  <p className="nota">{a.notas || tx.appliance.sinNotas}</p>
                )}
              </section>
            )}
          </>
        )}
      </Consulta>

      {codigo && a && <CodigoDeAlta codigo={codigo} nombre={a.nombre} alCerrar={() => setCodigo(null)} />}

      {confirmar === 'suspender' && a && (
        <Ventana
          titulo={tx.appliance.suspenderA(a.nombre)}
          boton={tx.appliance.suspender}
          peligro
          alCerrar={() => setConfirmar(null)}
          alEnviar={async () => {
            await pedir(`/appliances/${a.id}`, { metodo: 'PATCH', cuerpo: { estado: 'suspendido' } });
            setConfirmar(null);
            avisar(tx.appliance.suspendido(a.nombre));
            void refrescar('/appliances', '/resumen', '/bitacora');
          }}
        >
          <p className="nota">{tx.appliance.suspenderQue}</p>
        </Ventana>
      )}

      {confirmar === 'revocar' && a && (
        <Ventana
          titulo={tx.appliance.revocarA(a.nombre)}
          boton={tx.appliance.revocarBoton}
          peligro
          alCerrar={() => setConfirmar(null)}
          alEnviar={async () => {
            await pedir(`/appliances/${a.id}`, { metodo: 'PATCH', cuerpo: { estado: 'revocado' } });
            setConfirmar(null);
            avisar(tx.appliance.revocado(a.nombre));
            void refrescar('/appliances', '/resumen', '/bitacora');
          }}
        >
          <p className="alerta" role="alert">
            <b>{tx.appliance.revocarCorta}</b> {tx.appliance.revocarQue}
          </p>
          <p className="nota">{tx.appliance.revocarSigue}</p>
        </Ventana>
      )}

      {confirmar === 'borrar' && a && (
        <Ventana
          titulo={tx.appliance.borrarA(a.nombre)}
          boton={tx.appliance.borrar}
          peligro
          alCerrar={() => setConfirmar(null)}
          alEnviar={async () => {
            await pedir(`/appliances/${a.id}`, { metodo: 'DELETE' });
            setConfirmar(null);
            void refrescar('/appliances', '/resumen', '/bitacora');
            navegar('/appliances', { replace: true });
          }}
        >
          <p className="nota">{tx.appliance.borrarQue}</p>
        </Ventana>
      )}
    </div>
  );
}
